import { isAnswered, type ReportAnswer, type ReportAnswers } from './report-checks.js'

/**
 * Suggestions, and the arithmetic behind the one that claims a habit.
 *
 * Every assist in the filler is a suggestion and nothing writes itself. That is
 * decision 55, and it is not a UI preference: a report is a legal document that
 * somebody signs, and an answer that appeared without being chosen is one the
 * signer cannot be said to have made. So all four assists produce the same shape
 * — a value, a reason in plain words, and a source — and they all land in a sheet
 * that arrives ticked and costs one tap to reject.
 *
 * The mockup faked all of this with constants: `PREFILL`, `USUAL` and `HISTORY`
 * were hard-coded objects and `draftFor()` was a hand-written function per field.
 * Here `carry` and `history` come from the last report at the address, `usual`
 * comes from the caller's own recent answers, and `draft` comes from
 * `FormField.draftTemplate` — see `report-draft.ts`. None of them is a model and
 * none of them guesses.
 */

export type AssistSource = 'carry' | 'usual' | 'draft' | 'heard'

export type AssistSuggestion = {
  fieldKey: string
  value: ReportAnswer
  source: AssistSource
  /** Why it is being offered, in the words the sheet shows. */
  reason: string
}

/** One prior answer to one field, as history draws it. */
export type AnswerSample = {
  value: ReportAnswer
  /** When the report it came from was submitted. */
  at: Date | string
}

/**
 * Answers compared as the user sees them.
 *
 * A multi-select is a set, not a sequence: choosing the roof void before the
 * subfloor is the same answer as the other way round, and treating the order as
 * meaningful would split one habit into two and then find no habit at all.
 */
export function sameAnswer(a: ReportAnswer, b: ReportAnswer): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false
    if (a.length !== b.length) return false
    const left = [...a].map(String).sort()
    const right = [...b].map(String).sort()
    return left.every((item, index) => item === right[index])
  }

  if (a === null || a === undefined || b === null || b === undefined) return false
  return String(a) === String(b)
}

/** One answer as a line of text, for a suggestion's own description. */
export function answerText(value: ReportAnswer): string {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

export type Habit = {
  value: ReportAnswer
  /** How many of the samples were this answer. */
  count: number
  /** How many samples there were. */
  of: number
}

/**
 * The answer somebody almost always gives, or null.
 *
 * Two thresholds, and both matter. Fewer than `minSamples` is not a habit, it is
 * a coincidence — offering "you always say Moderate" off two reports is how an
 * assist teaches people to stop reading it. And below `minShare` the answer is
 * merely the most common of several, which is a different and much weaker claim
 * than the one the sheet makes.
 */
export function habitFrom(
  samples: AnswerSample[],
  { minSamples = 4, minShare = 0.7 }: { minSamples?: number; minShare?: number } = {},
): Habit | null {
  const answered = samples.filter((sample) => isAnswered(sample.value))
  if (answered.length < minSamples) return null

  const groups: Array<{ value: ReportAnswer; count: number }> = []

  for (const sample of answered) {
    const group = groups.find((candidate) => sameAnswer(candidate.value, sample.value))
    if (group) group.count += 1
    else groups.push({ value: sample.value, count: 1 })
  }

  // Ties go to the answer seen most recently, which `samples` is ordered by.
  // Picking arbitrarily would make the same screen offer different things on two
  // loads, and an assist that changes its mind is one nobody trusts.
  const best = groups.reduce((winner, group) => (group.count > winner.count ? group : winner))

  if (best.count / answered.length < minShare) return null

  return { value: best.value, count: best.count, of: answered.length }
}

/** "You picked this on 9 of your last 10." */
export function habitReason(habit: Habit): string {
  return habit.count === habit.of
    ? `You have answered this the same way on all ${habit.of} of your recent reports.`
    : `You picked this on ${habit.count} of your last ${habit.of}.`
}

/**
 * Suggestions worth showing, given what is already answered.
 *
 * A suggestion for a field that is already filled in is noise: the technician
 * has answered the question, and the screen asking them to reconsider is the
 * behaviour that gets assists switched off. The one exception is a suggestion
 * that matches what is there, which is dropped for the same reason.
 */
export function offerable(
  suggestions: AssistSuggestion[],
  answers: ReportAnswers,
): AssistSuggestion[] {
  return suggestions.filter((suggestion) => {
    const current = answers[suggestion.fieldKey]
    if (!isAnswered(suggestion.value)) return false
    if (sameAnswer(current, suggestion.value)) return false
    return !isAnswered(current)
  })
}
